import { Check, Minus, Star, Trophy } from "lucide-react";

const TOOL_ROWS = ["Online booking", "Live chat / text", "Reviews widget", "Google Ads tag", "Facebook / Meta pixel", "Other ad pixels", "Analytics", "Coupons / specials", "Financing", "Warranty promise"];
const label = (c) => c.name || c.url.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");
const Mark = ({ on }) => (on ? <Check className="w-4 h-4 text-green-600 mx-auto" /> : <Minus className="w-4 h-4 text-slate-300 mx-auto" />);
const List = ({ title, items, tone }) => (items?.length ? <div><p className={`text-[11px] font-bold uppercase tracking-wide ${tone}`}>{title}</p><ul className="text-sm text-slate-700 list-disc ml-4">{items.map((x, i) => <li key={i}>{x}</li>)}</ul></div> : null);

export const CompetitorResults = ({ data, categories }) => {
  const me = { ...data.customer, name: "You" };
  const ok = data.competitors.filter(c => !c.error);
  const cols = [me, ...ok];
  const rated = ok.some(c => c.rating);
  return (
    <div className="space-y-6" data-testid="competitor-results">
      {data.summary && <p className="text-sm text-slate-700 bg-purple-50 border border-purple-100 rounded-lg p-3" data-testid="competitor-summary">{data.summary}</p>}
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead><tr className="border-b border-slate-200 text-slate-500 text-xs"><th className="text-left py-2">Business</th><th>Overall</th>{categories.map(c => <th key={c} className="capitalize">{c}</th>)}{rated && <th>Google rating</th>}</tr></thead>
          <tbody>
            {cols.map((c, i) => (
              <tr key={i} className={`border-b border-slate-100 ${i === 0 ? "bg-blue-50 font-semibold" : ""}`} data-testid={`competitor-score-row-${i}`}>
                <td className="py-2 max-w-[220px] truncate">{i === 0 ? `You (${label(data.customer)})` : label(c)}</td>
                <td className="text-center font-bold">{c.overall_score}</td>
                {categories.map(k => <td key={k} className="text-center">{c.scores?.[k] ?? "-"}</td>)}
                {rated && <td className="text-center text-xs">{c.rating ? <span className="inline-flex items-center gap-0.5"><Star className="w-3 h-3 fill-amber-400 text-amber-400" />{c.rating} ({c.reviews || 0})</span> : "-"}</td>}
              </tr>
            ))}
            {data.competitors.filter(c => c.error).map((c, i) => <tr key={`e${i}`} className="border-b border-slate-100"><td className="py-2 truncate">{label(c)}</td><td colSpan={categories.length + (rated ? 2 : 1)} className="text-xs text-red-500">{c.error}</td></tr>)}
          </tbody>
        </table>
      </div>
      <div className="overflow-x-auto">
        <h3 className="font-semibold text-slate-900 mb-2">Tools & marketing they use</h3>
        <table className="w-full text-sm min-w-[560px]" data-testid="competitor-tools-table">
          <thead><tr className="border-b border-slate-200 text-xs text-slate-500"><th className="text-left py-2">Tool</th>{cols.map((c, i) => <th key={i} className="max-w-[120px] truncate px-1">{i === 0 ? "You" : label(c)}</th>)}</tr></thead>
          <tbody>
            {TOOL_ROWS.map(t => <tr key={t} className="border-b border-slate-100"><td className="py-1.5">{t}</td>{cols.map((c, i) => <td key={i}><Mark on={c.tools?.includes(t)} /></td>)}</tr>)}
            <tr className="border-b border-slate-100"><td className="py-1.5">Blog / tips</td>{cols.map((c, i) => <td key={i}><Mark on={c.has_blog} /></td>)}</tr>
            <tr><td className="py-1.5">Social profiles</td>{cols.map((c, i) => <td key={i} className="text-center text-xs text-slate-600">{c.social?.length || 0}</td>)}</tr>
          </tbody>
        </table>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {ok.map((c, i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-4 space-y-2" data-testid={`competitor-card-${i}`}>
            <p className="font-bold text-slate-900 truncate">{label(c)} <span className="text-xs font-normal text-slate-500">score {c.overall_score}</span></p>
            <a href={c.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 break-all">{c.url}</a>
            <List title="Services they promote" items={c.services} tone="text-slate-500" />
            <List title="Keywords they target" items={c.keywords} tone="text-slate-500" />
            <List title="Where they're ahead" items={c.strengths} tone="text-red-600" />
            <List title="Gaps you can exploit" items={c.weaknesses} tone="text-green-700" />
            {c.social?.length > 0 && <p className="text-xs text-slate-500">Social: {c.social.join(", ")}</p>}
          </div>
        ))}
      </div>
      {data.how_to_beat?.length > 0 && (
        <div className="bg-slate-900 text-white rounded-xl p-5" data-testid="competitor-how-to-beat">
          <h3 className="font-bold flex items-center gap-2 mb-2"><Trophy className="w-5 h-5 text-amber-400" />How to beat them</h3>
          <ol className="list-decimal ml-5 space-y-1 text-sm text-slate-200">{data.how_to_beat.map((x, i) => <li key={i}>{x}</li>)}</ol>
        </div>
      )}
    </div>
  );
};
